import {
    MapTemplateDatasetExtended,
    MapTemplateExtended,
    useCreateMapTemplateDatasetsMutation,
    useFetchMapTemplateExtendedQuery,
    DataSourceStudio,
    useFetchActiveDataSourcePermissionsQuery,
} from "@biggeo/bg-server-lib/studio";
import {
    BasicDataWithSelect,
    Button,
    LoadingBar,
    Menu,
} from "@biggeo/bg-ui/lab";
import { AddOutline } from "@biggeo/bg-ui/lab/icons";
import { toNonReadonlyArray, updateSelected } from "@biggeo/bg-utils";
import { match } from "@vividtheory/remotedata";
import includes from "lodash/includes";
import { useState } from "react";
import { useNavigate } from "react-router";
import { pipe } from "fp-ts/lib/function";
import * as A from "fp-ts/Array";

import ErrorPage from "../../common/components/ErrorPage";
import AddDatasetWithSubMenu from "../../components/AddDatasetWithSubMenu/AddDatasetWithSubMenu";
import { useUserContext } from "../../common/components/UserContextProvider";
import { getDefaultDatasetStyles } from "../../map/mapbox/context/utils/general-utils";

export const AddDatasetSubMenuContainer = ({
    existingDatasets,
    mapTemplateId,
    setRemote,
    localDataList,
    setLocalDataList,
}: {
    readonly existingDatasets: readonly DataSourceStudio[];
    readonly mapTemplateId: string;
    readonly setRemote: (v: readonly MapTemplateDatasetExtended[]) => void;
    readonly localDataList: string[];
    readonly setLocalDataList: (v: string[]) => void;
}) => {
    const { organization } = useUserContext();

    const { remote } = useFetchActiveDataSourcePermissionsQuery(
        organization
            ? {
                  variables: {
                      input: {
                          fkOrganizationId: organization.id,
                          datascape: true,
                      },
                  },
              }
            : {
                  skip: true,
              }
    );

    const isInExistingDatasets = (dataset: DataSourceStudio) => {
        return existingDatasets.some((existing) => existing.id === dataset.id);
    };

    const toPage = useNavigate();

    const [map, setMap] = useState<MapTemplateExtended>();
    const [open, setOpen] = useState(false);

    useFetchMapTemplateExtendedQuery({
        variables: {
            id: mapTemplateId,
        },
        onCompleted: (data) => setMap(data.fetchMapTemplateExtended),
    });

    const {
        executeMutation: createMapTemplateDatasets,
        mutationReturn: [_, { loading }],
    } = useCreateMapTemplateDatasetsMutation();

    const save = (fkDataSourceIds: string[]) => {
        createMapTemplateDatasets({
            variables: {
                input: fkDataSourceIds.map((fkDataSourceId) => ({
                    fkDataSourceId,
                    fkMapTemplateId: mapTemplateId,
                    mapUse: true,
                    styles: getDefaultDatasetStyles(),
                })),
            },
            onCompleted: (data) => {
                setLocalDataList([]);
                setOpen(false);
                setRemote(data.createMapTemplateDatasets);
            },
        });
    };

    return (
        <Menu
            placement="bottom-start"
            sx={{
                maxWidth: 72,
                width: "100%",
                gap: 0,
                padding: 0,
            }}
            open={open}
            onOpenChange={setOpen}
            content={match(remote, {
                _: () => <LoadingBar />,
                Success: (data) => {
                    const mapTemplateDatasets = map?.mapTemplateDatasets || [];

                    const mapTemplateDataSources = map?.dataSources || [];

                    const datasetIdsWithMapUseFalse = mapTemplateDatasets
                        .filter((m) => !m.mapUse)
                        .map((m) => m.fkDataSourceId);

                    const filteredDataSources: BasicDataWithSelect[] = pipe(
                        data.fetchActiveDataSourcePermissions,
                        toNonReadonlyArray,
                        A.filter(
                            (connection) =>
                                !isInExistingDatasets(connection.dataSource) &&
                                !datasetIdsWithMapUseFalse.includes(
                                    connection.dataSource.id
                                )
                        ),
                        /* Filter out duplicate data_sources for the scenario where the user:
                            1. Turns data_source to hot (adds a row in data_source_permissions with fk_user_id as NULL)
                            2. Adds it as map_template_dataset
                            3. Buys the same data_source (adds a row in data_source_permissions with fk_user_id as defined)
                            4. In the list of data sources they can add, we prioritize the bought one and hide the first one, which is the one they already added.
                         This filter is to stop them from adding the same data_source as a map_template_dataset
                        */
                        A.filter(
                            (dataset) =>
                                !mapTemplateDataSources
                                    .map((d) => d.id)
                                    .includes(dataset.dataSource.id)
                        ),
                        A.map((item) => ({
                            id: item.dataSource.id,
                            image: undefined,
                            title:
                                item.dataSource.name ||
                                item.dataSource.tableName,
                            select: includes(localDataList, item.dataSource.id),
                        }))
                    );

                    const handleOnCheck = (
                        selectedData: BasicDataWithSelect
                    ) => {
                        setLocalDataList(
                            updateSelected(selectedData.id, localDataList)
                        );
                    };

                    return (
                        <AddDatasetWithSubMenu
                            dataSources={filteredDataSources}
                            localDataList={localDataList}
                            handleOnCheck={handleOnCheck}
                            loading={loading}
                            save={save}
                            toPage={toPage}
                        />
                    );
                },
                Failure: (err) => <ErrorPage subtitle={err.message} />,
            })}
        >
            <Button
                variant="outlined"
                density="dense"
                startNode={<AddOutline size="xs" />}
            >
                Add Dataset
            </Button>
        </Menu>
    );
};
